// eslint-disable-line
import React from 'react';
import { FormSection } from 'redux-form';
import { connect } from 'react-redux';
import * as validators from 'jbc-front/utils/validators';
import { toAdYearDate } from 'jbc-front/utils/jpYear';
import _ from 'lodash';
import moment from 'moment';
import { TextField, DateField, RadioField } from 'src/jbc-front/components/Form';
import ExtensionSelectField from 'src/components/ExtensionSelectField';
import { isDateStringFormat, isStaffCode } from 'src/utils/CustomValidators';
import selector, { getOptionLabel } from 'src/utils/Utils';
import { getEmployeeFormValues, updateEmployeeForm, getEmployeeData } from 'src/reducers/employees/employees';
import { getSelectOptions, getRadioOptions } from 'src/reducers/selectOptions';
import { dateSlide, formatDate } from 'src/utils/Date';
import isUseOtherApp from 'src/utils/JbcId';
import EmployeeLeaveOfAbsences from './EmployeeLeaveOfAbsences';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

const BusinessInfoSection = (props) => {
  const {
    employmentTypes,
    groups,
    positions,
    occupations,
    enrollmentStatuses,
    employeeInsurance,
    resignedOn,
    resignedReasons,
    updateInsuranceAcquisitionOn,
    updateInsuranceExpiredOn,
    client,
    offices,
    resignTaxOptions,
    selectedJoinedOn,
  } = props;
  const { t } = useTranslation();

  return (
    <div>
      <FormSection name="employee">
        <TextField
          label={t('features.Employees.components.BusinessInfoSection.staff_code')}
          type="text"
          name="staffCode"
          maxLength="50"
          validate={isStaffCode}
          required
        />
        {isUseOtherApp && (
          <p className="u-mb20">
            {t(
              'features.Employees.components.BusinessInfoSection.changed_staff_code_will_also_be_reflected_in_other_JOBCAN_services_you_are_using'
            )}
          </p>
        )}

        <ExtensionSelectField
          label={t('features.Employees.components.BusinessInfoSection.belong')}
          name="clientBranchId"
          options={offices}
        />

        <ExtensionSelectField
          label={t('features.Employees.components.BusinessInfoSection.employment_type')}
          name="employmentTypeId"
          options={employmentTypes}
        />

        <ExtensionSelectField
          label={t('features.Employees.components.BusinessInfoSection.position')}
          name="positionId"
          options={positions}
        />

        <ExtensionSelectField
          label={t('features.Employees.components.BusinessInfoSection.occupation')}
          name="occupationId"
          options={occupations}
        />

        <ExtensionSelectField
          label={t('features.Employees.components.BusinessInfoSection.group')}
          name="groupId"
          options={groups}
        />

        <TextField
          disabled
          label={t('features.Employees.components.BusinessInfoSection.employment_status')}
          name="enrollmentStatus"
          format={(value) => getOptionLabel(value)(enrollmentStatuses)}
        />

        <DateField
          required
          label={t('features.Employees.components.BusinessInfoSection.first_day_of_work')}
          viewMode="months"
          name="joinedOn"
          validate={isDateStringFormat}
          onChange={updateInsuranceAcquisitionOn}
        />

        <DateField
          label={t('features.Employees.components.BusinessInfoSection.retirement_date')}
          viewMode="months"
          name="resignedOn"
          validate={isDateStringFormat}
          onChange={(_fields, expiredOn) => updateInsuranceExpiredOn(expiredOn, employeeInsurance)}
        />

        {resignedOn && (
          <RadioField
            label={t('features.Employees.components.BusinessInfoSection.retirement_division')}
            name="resignedReason"
            options={resignedReasons}
          />
        )}
        {resignedOn && (
          <RadioField
            label={t('features.Employees.components.BusinessInfoSection.resign_tax_options')}
            name="resignTaxOption"
            options={resignTaxOptions}
          />
        )}
        <EmployeeLeaveOfAbsences />
      </FormSection>
    </div>
  );
};

const mapStateToProps = (state) => ({
  groups: getSelectOptions(state, 'groups'),
  positions: getSelectOptions(state, 'positions'),
  occupations: getSelectOptions(state, 'occupations'),
  employmentTypes: getSelectOptions(state, 'employmentTypes'),
  enrollmentStatuses: getSelectOptions(state, 'enrollmentStatuses'),
  resignedReasons: getRadioOptions(state, 'resignedReasons'),
  resignedOn: selector(getEmployeeFormValues(state), 'employee', 'resignedOn'),
  employeeInsurance: selector(getEmployeeFormValues(state), 'employeeInsurance') || {},
  selectedJoinedOn: selector(getEmployeeFormValues(state), 'employee', 'joinedOn'),
  client: getSelectOptions(state, 'client'),
  offices: getSelectOptions(state, 'offices'),
  resignTaxOptions: getSelectOptions(state, 'resignTaxOptions'),
});

const mapDispatchToProps = (dispatch) => ({
  updateInsuranceAcquisitionOn: (_fields, acquisitionOn) => {
    dispatch(updateEmployeeForm('employeeInsurance.healthCertificateAcquisitionOn', acquisitionOn));
    dispatch(updateEmployeeForm('employeeInsurance.pensionCertificateAcquisitionOn', acquisitionOn));
    dispatch(updateEmployeeForm('employeeInsurance.employmentCertificateAcquisitionOn', acquisitionOn));
  },
  updateInsuranceExpiredOn: (expiredOn, employeeInsurance) => {
    if (!employeeInsurance.healthCertificateExpiredOn) {
      dispatch(updateEmployeeForm('employeeInsurance.healthCertificateExpiredOn', formatDate(dateSlide(expiredOn, 1))));
    }
    if (!employeeInsurance.pensionCertificateExpiredOn) {
      dispatch(
        updateEmployeeForm('employeeInsurance.pensionCertificateExpiredOn', formatDate(dateSlide(expiredOn, 1)))
      );
    }
    if (!employeeInsurance.employmentCertificateExpiredOn) {
      dispatch(updateEmployeeForm('employeeInsurance.employmentCertificateExpiredOn', expiredOn));
    }
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(BusinessInfoSection);

const sameDayIn = (absence1, absence2) => {
  const startOn = moment(absence1.startOn);

  if (startOn.isSame(absence2.startOn)) {
    return true;
  }
  if (startOn.isAfter(absence2.startOn)) {
    return !absence2.endOn || startOn.isSameOrBefore(absence2.endOn);
  }
  return !absence1.endOn || moment(absence2.startOn).isSameOrBefore(absence1.endOn);
};

const validatePeriods = (values, props) => {
  const name = 'employee.employeeLeaveOfAbsences';
  const periods = _.get(values, name);
  const joinedAt = _.get(values, 'employee.joinedOn');
  const resignedAt = _.get(values, 'employee.resignedOn');
  if (!periods) {
    return {};
  }

  const errors = {};
  _.forEach(periods, (period, idx) => {
    const startOn =
      period.startOn && period.startOn.match(validators.jpYearRegExp)
        ? toAdYearDate(period.startOn, false)
        : period.startOn;
    const endOn =
      period.endOn && period.endOn.match(validators.jpYearRegExp) ? toAdYearDate(period.endOn, false) : period.endOn;

    if (!startOn) {
      _.set(
        errors,
        `${name}[${idx}].startOn`,
        /*'を入力してください'*/ i18next.t('features.Employees.components.BusinessInfoSection.please_input')
      );
    } else if (
      moment(startOn).isValid() &&
      joinedAt &&
      moment(joinedAt).isValid() &&
      moment(startOn).isBefore(joinedAt)
    ) {
      _.set(
        errors,
        `${name}[${idx}].startOn`,
        i18next.t('features.Employees.components.BusinessInfoSection.must_be_a_date_after_the_first_day_of_work')
      );
    }

    if (endOn && moment(endOn).isValid()) {
      if (startOn && moment(startOn).isValid() && moment(startOn).isAfter(endOn)) {
        _.set(
          errors,
          `${name}[${idx}].endOn`,
          i18next.t('features.Employees.components.BusinessInfoSection.must_be_a_date_after_the_start_date')
        );
      } else if (resignedAt && moment(resignedAt).isValid() && moment(endOn).isAfter(resignedAt)) {
        _.set(
          errors,
          `${name}[${idx}].endOn`,
          i18next.t('features.Employees.components.BusinessInfoSection.must_be_a_date_before_the_retirement_date')
        );
      }
    }
  });

  if (!_.isEmpty(errors)) {
    return errors;
  }

  _.forEach(periods, (period, idx) => {
    const startOn =
      period.startOn && period.startOn.match(validators.jpYearRegExp)
        ? toAdYearDate(period.startOn, false)
        : period.startOn;
    for (let i = 0; i < idx; i += 1) {
      if (startOn) {
        if (sameDayIn(periods[i], period)) {
          _.set(
            errors,
            `${name}[${idx}].startOn`,
            i18next.t('features.Employees.components.BusinessInfoSection.overlap_with_existing_period')
          );
          _.set(
            errors,
            `${name}[${idx}].endOn`,
            i18next.t('features.Employees.components.BusinessInfoSection.overlap_with_existing_period')
          );
        }
      }
    }
  });
  return errors;
};



export const validate = (values, props) => Object.assign({}, validatePeriods(values, props));
